import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getNavbarHeight, getViewportDimensions } from '../../../../models/Utils';
import { storyCardTypes } from '../../../../models/Models';
import {
  setActiveSlideKey,
  setActiveSlideType
} from '../../../../store/reducers/mainReducer';

import { ADALandmarks, ADAARIARoles } from '../../../../models/ADA';

import CardHolder from '../../../Common/CardHolder/CardHolder';
import StoryCarousel from '../../../DigitalMuseum/StoryCarousel/StoryCarousel';
import QuoteStory from '../../../DigitalMuseum/QuoteStory/QuoteStory';
import AudioClip from '../../../DigitalMuseum/AudioClip/AudioClip';
import Video from '../../../DigitalMuseum/Video/Video';
import UserPoll from '../../../DigitalMuseum/UserPoll/UserPoll';
import InfographicCarousel from '../../../DigitalMuseum/InfographicCarousel/InfographicCarousel';

import { useWindowSize } from '../../../../models/Hooks';
import { useMediaQuery } from 'react-responsive';

import './ThemeContent.scss';

const ThemeContent = ({ cards, darkMode }) => {
  const el = useRef(null);
  const menuOpened = useSelector(state => state.data.menuOpened);
  const dispatch = useDispatch();

  const cardElementRefs = useRef([]);
  const cardDataRefs = useRef([]);
  const activeCardIndex = useRef(null);

  const isFullScreen =  window.innerHeight == window.screen.height;

  /* Handling new inline layout */
  const size = useWindowSize();
  const isLandscapeWithMargin = useMediaQuery({ query: '(min-width: 1274px)' });
  const isLandscapeNoMargin = useMediaQuery({
    query: '(orientation: landscape) and (max-width: 1274px)',
  });
  const aspectRatio = 2;
  const maxColumnWidth = 1234;
  const cardPadding = 30;
  const isInlineLayout = isLandscapeWithMargin || (isLandscapeNoMargin && !isFullScreen); // fullscreenElementHelper() ? false : 

  const cardWidth = isInlineLayout ? Math.min(maxColumnWidth, size.width - 32) : size.width;
  const cardHeight = isInlineLayout
    ? Math.min(size.height * 0.7, cardWidth / aspectRatio)
    : size.height - getNavbarHeight() - 40; // based on CSS rule for the margin

  // const cardWidth = 900;
  // const cardHeight = 600;

  const handleScroll = () => {
    if (cardElementRefs.current) {
      const viewport = getViewportDimensions();
      const midScreen = (viewport.height + getNavbarHeight()) / 2;
      for (let i = 0; i < cardElementRefs.current.length; i++) {
        if (i === activeCardIndex.current) continue;

        const rect = cardElementRefs.current[i].getBoundingClientRect();
        if (rect.top <= midScreen && rect.top + rect.height > midScreen) {
          setActiveCard(i);
        }
      }
    }
  };

  const handleResize = () => {
    
  };

  const setActiveCard = (index) => {
    activeCardIndex.current = index;
    dispatch(setActiveSlideKey(cardDataRefs.current[index].Key));
    dispatch(setActiveSlideType(cardDataRefs.current[index].Type));
  };

  useLayoutEffect(() => {
    handleScroll();

    document.body.addEventListener('scroll', handleScroll);

    return () => {
      document.body.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useLayoutEffect(() => {
    const dotGroups = el.current.getElementsByClassName('carousel__dot-group');
    for (let i = 0; i < dotGroups.length; i++) {
      const dotGroup = dotGroups[i];
      const j = i;
      setTimeout(() => {
        if (dotGroup.children && dotGroup.children.length) {
          dotGroup.children[0].click();
        }
      }, 50);
    }
    cardDataRefs.current = cards;
    if (el.current) cardElementRefs.current = el.current.getElementsByClassName('ccw-card-holder');
  }, [cards]);

  const getHtmlFromCard = (card, isInlineLayout, size) => {
    let value;
    switch (card.Type) {
      case storyCardTypes.VIDEO_INTRO:
      case storyCardTypes.OBJECT_3D:
        throw new Error(`Card type "${card.Type}" is not supported in this segment`);
      case storyCardTypes.TEXT_CAROUSEL:
      case storyCardTypes.TEXT_CAROUSEL_WITH_BG:
      case storyCardTypes.TEXT_IMAGE_CAROUSEL:
      case storyCardTypes.TEXT_VIDEO_CAROUSEL:
      case storyCardTypes.MIXED_MEDIA_CAROUSEL:
        value = (
          <StoryCarousel
            cardData={card}
            isInline={isInlineLayout}
            size={size}
            forceInlineImage={true}
          />
        );
        break;
      case storyCardTypes.INFOGRAPHIC_CAROUSEL:
        value = <InfographicCarousel cardData={card} isInline={isInlineLayout} />;
        break;
      case storyCardTypes.QUOTE:
        value = <QuoteStory cardData={card} isInline={isInlineLayout} />;
        break;
      case storyCardTypes.VIDEO:
        value = (
          <Video
            cardData={card}
            isInline={isInlineLayout}
            size={size}
          />
        );
        break;
      case storyCardTypes.AUDIO_PODCAST:
      case storyCardTypes.AUDIO_MISC:
        value = <AudioClip
          cardData={card}
          isInline={isInlineLayout}
          size={size}
        />;
        break;
      case storyCardTypes.USER_POLL:
        value = <UserPoll cardData={card} isInline={isInlineLayout} />;
        break;
      default:
        throw new Error('No matching ID for component. See StorySelector.js');
        break;
    }

    return value;
  };

  const handleClickOnCard = (index) => {
    setActiveCard(index);
  };

  const generateCards = (cards) => {
    let retValue = [];
    const isInlineLayout = true;
    cards.forEach((card, index) => {
      retValue.push(
        <div className="cpvi-grid-row" key={index}>
          <div className="cpvi-grid-col w12" onClick={() => handleClickOnCard(index)}>
            <CardHolder
              hasArrow={false}
              isLightBg={false}
              isInlineCard={isInlineLayout}
              cardHeight={cardHeight}
              active={false}
              specialClass='ccw-card-holder'
            >
              {getHtmlFromCard(card, isInlineLayout, { width: cardWidth, height: cardHeight })}
            </CardHolder>
          </div>
        </div>
      );
    });

    return retValue;
  };

  return (
    <div className={`ccw-theme-container ${darkMode ? 'dark-mode' : ''}`} ref={el}>
      <div className="cpvi-grid-container">
            {generateCards(cards)}
      </div>
    </div>
  );
};

export default ThemeContent;